import { useState } from "react";
import { toast } from "react-toastify";
import { FaTrash, FaSyncAlt } from "react-icons/fa";
import { axiosClient } from "../configs/axiosClient";
import { API } from "../configs/endpoints";

const QUICK_PATHS = [
  { label: "Trang chủ", path: "/" },
  { label: "Tất cả sản phẩm", path: "/product" },
  { label: "Bài viết", path: "/article" },
];

export const Cache = () => {
  const [customPath, setCustomPath] = useState("");
  const [loading, setLoading] = useState<string | null>(null);
  const [results, setResults] = useState<
    { path: string; success: boolean; time: string }[]
  >([]);

  const revalidate = async (path: string) => {
    setLoading(path);
    try {
      const res = await axiosClient.post(API.cache.revalidate, { path });
      const data = res.data;
      if (data.revalidated) {
        toast.success(`Đã xóa cache: ${path}`);
        setResults((prev) => [
          {
            path,
            success: true,
            time: new Date().toLocaleTimeString("vi-VN"),
          },
          ...prev,
        ]);
      } else {
        toast.error(data.message || "Xóa cache thất bại");
        setResults((prev) => [
          {
            path,
            success: false,
            time: new Date().toLocaleTimeString("vi-VN"),
          },
          ...prev,
        ]);
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Không thể kết nối đến server";
      toast.error(msg);
      setResults((prev) => [
        {
          path,
          success: false,
          time: new Date().toLocaleTimeString("vi-VN"),
        },
        ...prev,
      ]);
    } finally {
      setLoading(null);
    }
  };

  const revalidateAll = async () => {
    setLoading("all");
    const paths = ["/", "/product", "/article"];
    for (const p of paths) {
      await revalidate(p);
    }
    setLoading(null);
    toast.success("Đã xóa cache toàn bộ trang chính");
  };

  const handleCustomSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const path = customPath.trim();
    if (!path) return;
    revalidate(path.startsWith("/") ? path : `/${path}`);
    setCustomPath("");
  };

  return (
    <div className="p-5 space-y-5">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Quản lý Cache</h1>
        <p className="text-sm text-gray-500 mt-1">
          Xóa cache trang web khi cập nhật sản phẩm, danh mục, bài viết...
        </p>
      </div>

      {/* Xóa toàn bộ */}
      <div className="bg-white border border-gray-100 rounded-xl p-5 space-y-3">
        <h2 className="text-sm font-semibold text-gray-800">
          Xóa cache toàn bộ
        </h2>
        <p className="text-xs text-gray-400">
          Xóa cache tất cả trang chính (Trang chủ, Sản phẩm, Bài viết)
        </p>
        <button
          type="button"
          onClick={revalidateAll}
          disabled={loading === "all"}
          className="flex items-center gap-2 px-4 py-2.5 bg-red-500 hover:bg-red-600 text-white text-sm font-medium rounded-lg cursor-pointer transition-colors disabled:opacity-50"
        >
          {loading === "all" ? (
            <FaSyncAlt className="animate-spin" />
          ) : (
            <FaTrash />
          )}
          {loading === "all" ? "Đang xóa..." : "Xóa toàn bộ cache"}
        </button>
      </div>

      {/* Xóa nhanh */}
      <div className="bg-white border border-gray-100 rounded-xl p-5 space-y-3">
        <h2 className="text-sm font-semibold text-gray-800">Xóa cache nhanh</h2>
        <div className="flex flex-wrap gap-2">
          {QUICK_PATHS.map((item) => (
            <button
              key={item.path}
              type="button"
              onClick={() => revalidate(item.path)}
              disabled={loading === item.path}
              className="flex items-center gap-2 px-3 py-2 border border-gray-200 hover:border-blue-500 hover:text-blue-500 text-sm text-gray-700 rounded-lg cursor-pointer transition-colors disabled:opacity-50"
            >
              {loading === item.path ? (
                <FaSyncAlt className="animate-spin text-xs" />
              ) : (
                <FaTrash className="text-xs" />
              )}
              {item.label}
            </button>
          ))}
        </div>
      </div>

      {/* Xóa theo link */}
      <div className="bg-white border border-gray-100 rounded-xl p-5 space-y-3">
        <h2 className="text-sm font-semibold text-gray-800">
          Xóa cache theo đường dẫn
        </h2>
        <p className="text-xs text-gray-400">
          Ví dụ: <code className="bg-gray-100 px-1 rounded">/product/may-khoan-bosch</code> hoặc{" "}
          <code className="bg-gray-100 px-1 rounded">/article/bai-viet-moi</code>
        </p>
        <form onSubmit={handleCustomSubmit} className="flex gap-2">
          <input
            type="text"
            value={customPath}
            onChange={(e) => setCustomPath(e.target.value)}
            placeholder="/product/slug-san-pham"
            className="flex-1 p-2 border border-gray-200 rounded-lg focus:outline-none focus:border-blue-500 placeholder:text-sm placeholder:text-gray-400 text-sm"
          />
          <button
            type="submit"
            disabled={!customPath.trim() || loading !== null}
            className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium rounded-lg cursor-pointer transition-colors disabled:opacity-50"
          >
            Xóa cache
          </button>
        </form>
      </div>

      {/* Lịch sử */}
      {results.length > 0 && (
        <div className="bg-white border border-gray-100 rounded-xl p-5 space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-gray-800">Lịch sử</h2>
            <button
              type="button"
              onClick={() => setResults([])}
              className="text-xs text-gray-400 hover:text-red-500 cursor-pointer"
            >
              Xóa lịch sử
            </button>
          </div>
          <div className="space-y-1 max-h-60 overflow-y-auto">
            {results.map((r, i) => (
              <div
                key={i}
                className="flex items-center justify-between py-1.5 px-2 rounded-lg hover:bg-gray-50 text-sm"
              >
                <div className="flex items-center gap-2">
                  <span
                    className={`w-2 h-2 rounded-full ${r.success ? "bg-green-500" : "bg-red-500"}`}
                  />
                  <code className="text-gray-700">{r.path}</code>
                </div>
                <span className="text-xs text-gray-400">{r.time}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
